import React, { useEffect, useRef, useState } from "react";

type SignaturePadProps = {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
};

const SignaturePad = ({ value, onChange }: SignaturePadProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [hasStroke, setHasStroke] = useState(Boolean(value));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const nextWidth = Math.max(Math.floor(rect.width * ratio), 1);
      const nextHeight = Math.max(Math.floor(rect.height * ratio), 1);
      if (canvas.width !== nextWidth || canvas.height !== nextHeight) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
          ctx.lineWidth = 2;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.strokeStyle = "#212529";
        }
      }
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    if (!value) {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setHasStroke(false);
    }
  }, [value]);

  const getPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setHasStroke(false);
    onChange(null);
  };

  const finishStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) {
      return;
    }
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    try {
      canvas.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
    onChange(canvas.toDataURL("image/png"));
  };

  return (
    <div className="guardian-signature">
      <div className="guardian-signature__pad-wrap">
        <canvas
          ref={canvasRef}
          className="guardian-signature__canvas"
          onPointerDown={(event) => {
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext("2d");
            if (!canvas || !ctx) {
              return;
            }
            event.preventDefault();
            canvas.setPointerCapture(event.pointerId);
            drawingRef.current = true;
            const point = getPoint(event);
            ctx.beginPath();
            ctx.moveTo(point.x, point.y);
          }}
          onPointerMove={(event) => {
            if (!drawingRef.current) {
              return;
            }
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext("2d");
            if (!canvas || !ctx) {
              return;
            }
            const point = getPoint(event);
            ctx.lineTo(point.x, point.y);
            ctx.stroke();
            setHasStroke(true);
          }}
          onPointerUp={finishStroke}
          onPointerCancel={finishStroke}
        />
        {!hasStroke && (
          <span className="guardian-signature__placeholder">
            계약서
            <br />
            서명
          </span>
        )}
      </div>
      <button className="guardian-signature__reset" type="button" onClick={clear}>
        다시 서명
      </button>
    </div>
  );
};

export default SignaturePad;
